import React, { useState } from "react";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, ArrowRight, Truck, LogOut, Search } from "lucide-react";
import { toast } from "sonner";

const Field = ({ label, children, req }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}{req && <span className="text-rose-500"> *</span>}</label>
    {children}
  </div>
);

const swatch = (name) => {
  const g = [
    "from-primary/25 to-sky-500/15 text-primary border-primary/30",
    "from-emerald-500/25 to-teal-500/15 text-emerald-500 border-emerald-500/30",
    "from-amber-500/25 to-orange-500/15 text-amber-500 border-amber-500/30",
    "from-cyan-500/25 to-blue-500/15 text-cyan-500 border-cyan-500/30",
    "from-violet-500/25 to-fuchsia-500/15 text-violet-400 border-violet-500/30",
    "from-rose-500/25 to-pink-500/15 text-rose-500 border-rose-500/30",
  ];
  let h = 0; for (const c of name || "") h = (h + c.charCodeAt(0)) % g.length;
  return g[h];
};

export default function CompanySelect() {
  const { companies, setCompany, reloadCompanies } = useApp();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const empty = { name: "", gstin: "", phone: "", email: "", address: "", tagline: "" };
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!f.name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/companies", f);
      const cs = await reloadCompanies();
      toast.success(`${data.name} created`);
      setOpen(false); setF(empty);
      if (cs.find((c) => c.id === data.id)) setCompany(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create company");
    } finally { setSaving(false); }
  };

  const filtered = companies.filter((c) => !q || (c.name || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 sm:py-12 relative overflow-hidden bg-[#0b1220]">
      {/* Animated backdrop */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
      <motion.div className="pointer-events-none absolute -top-40 -left-20 h-[520px] w-[520px] rounded-full bg-[#f2791a]/20 blur-[140px]"
        animate={{ x: [0, 40, 0], y: [0, 30, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div className="pointer-events-none absolute -bottom-40 -right-20 h-[560px] w-[560px] rounded-full bg-[#1e3a6e]/40 blur-[150px]"
        animate={{ x: [0, -40, 0], y: [0, -30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative z-10 w-full max-w-5xl mx-auto flex items-center justify-between">
        <span className="text-xs font-semibold tracking-widest uppercase text-white/40">TruckLink · Accounting Platform</span>
        <button onClick={logout} data-testid="company-logout" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 w-full max-w-5xl mx-auto mt-6 flex flex-col items-center text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, ease: "easeOut" }}
          className="rounded-3xl bg-white p-4 sm:p-5 shadow-2xl ring-1 ring-white/10">
          <img src="/trucklink-logo.png" alt="TruckLink" className="h-24 sm:h-32 w-auto" data-testid="brand-logo" />
        </motion.div>

        <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-8 text-white">Choose your workspace</motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.18 }}
          className="text-white/55 mt-3 max-w-xl">Each company keeps its own hubs, fleet, and accounts — fully isolated. Pick one to jump in.</motion.p>

        <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 w-full max-w-sm sm:max-w-md">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input className="pl-9 h-11 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-[#f2791a]" placeholder="Search companies" value={q} onChange={(e) => setQ(e.target.value)} data-testid="company-search" />
          </div>
          <span className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold text-white/60 bg-white/5 border border-white/10 rounded-full px-3 py-2" data-testid="workspace-count">
            <Building2 className="h-3.5 w-3.5" /> {companies.length} workspace{companies.length === 1 ? "" : "s"}
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8 w-full text-left">
          {filtered.map((c, i) => (
            <motion.button
              key={c.id}
              data-testid={`company-card-${c.id}`}
              onClick={() => setCompany(c.id)}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
              whileHover={{ y: -5 }} whileTap={{ scale: 0.98 }}
              className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-left group relative overflow-hidden shadow-lg hover:border-[#f2791a]/60 hover:bg-white/[0.07] transition-all flex flex-col min-h-[210px]"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#1e3a6e] via-[#f2791a] to-[#ffb020] opacity-0 group-hover:opacity-100 transition-opacity" />
              {(c.logo_full || c.logo) ? (
                <div className="h-24 rounded-xl bg-white flex items-center justify-center mb-4 overflow-hidden p-3">
                  <img src={c.logo_full || c.logo} alt={c.name} className="max-h-full max-w-full object-contain" />
                </div>
              ) : (
                <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${swatch(c.name)} border flex items-center justify-center mb-4 text-xl font-bold font-display`}>
                  {(c.name || "?").trim().charAt(0).toUpperCase()}
                </div>
              )}
              <div className="font-display font-semibold text-lg leading-tight text-white">{c.name}</div>
              <div className="text-xs text-white/45 mt-1 line-clamp-1">{c.tagline || c.address || "Trucking & logistics"}</div>
              <div className="mt-auto pt-4 flex items-center justify-between text-sm font-medium text-white/45 group-hover:text-[#f2791a] transition-colors">
                <span>Open workspace</span>
                <span className="h-7 w-7 rounded-full bg-white/10 group-hover:bg-[#f2791a]/20 flex items-center justify-center transition-colors"><ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
              </div>
            </motion.button>
          ))}

          {filtered.length === 0 && q && (
            <div className="col-span-full text-center text-white/40 py-10" data-testid="no-company-match">No company matches “{q}”.</div>
          )}

          <motion.button
            data-testid="add-company-card"
            onClick={() => setOpen(true)}
            whileHover={{ y: -5 }} whileTap={{ scale: 0.98 }}
            className="rounded-2xl p-5 border-2 border-dashed border-white/15 hover:border-[#f2791a]/60 flex flex-col items-center justify-center gap-3 text-white/50 hover:text-[#f2791a] transition-colors min-h-[210px]"
          >
            <div className="h-14 w-14 rounded-xl bg-white/5 flex items-center justify-center"><Plus className="h-7 w-7" /></div>
            <span className="text-sm font-semibold">Add Company</span>
          </motion.button>
        </div>
      </motion.div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="add-company-dialog">
          <DialogHeader><DialogTitle>Add Company</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Field label="Company Name" req><Input data-testid="company-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field></div>
            <Field label="GSTIN"><Input data-testid="company-gstin-input" value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })} /></Field>
            <Field label="Phone"><Input data-testid="company-phone-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></Field>
            <Field label="Email"><Input data-testid="company-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
            <Field label="Tagline"><Input data-testid="company-tagline-input" value={f.tagline} onChange={(e) => setF({ ...f, tagline: e.target.value })} /></Field>
            <div className="col-span-2"><Field label="Address"><Input data-testid="company-address-input" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setF(empty)}>Reset</Button>
            <Button onClick={create} disabled={saving} data-testid="save-company-button">{saving ? "Creating…" : "Create & Enter"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
